import React, { useState } from 'react';
import { SUMMARY_ITEM_CATEGORY, type Summary } from '../../types/messages';
import { formatRouteDate } from '../../utils/routeInfoFormatter';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import { createTripDuplicateLog } from '../../api/tripDuplicateLog';

interface Props {
  tripId: string;
  text: string;
  summary: Summary;
  includeTax: boolean;
  onArrangementRequest: () => void;
  isRequesting: boolean;
  isRequested: boolean;
  isPaymentInvalid?: boolean;
}

// AIチャット予約にはまだレンタカーなどはないが、既存の/selectなどの予約からは存在するため、重複表示としてのラベルは用意が必要
const ELEMENT_TYPE_LABELS: Record<string, string> = {
  hotel: 'ホテル',
  shinkansen: '新幹線',
  express: '特急',
  domestic_air: '国内航空券',
  foreign_air: '海外航空券',
  rental_car: 'レンタカー',
  taxi: 'タクシー',
  bus: 'バス',
  railway_ticket: '在来線'
};

const ArrangementConfirmationMessage = ({
  tripId,
  text,
  summary,
  includeTax,
  onArrangementRequest,
  isRequesting,
  isRequested,
  isPaymentInvalid = false
}: Props) => {
  const styles = getStyles();
  const [agreed, setAgreed] = useState(false);
  const [duplicationAgreed, setDuplicationAgreed] = useState(false);
  const { items, totalPrice, totalPriceWithTax, tripDuplications } = summary;
  const displayTotal = includeTax ? totalPriceWithTax : totalPrice;

  const hasDuplications = tripDuplications.length > 0;
  const buttonDisabled =
    !agreed || (hasDuplications && !duplicationAgreed) || isRequesting || isRequested || isPaymentInvalid;
  const buttonText = isRequested ? '手配依頼済み' : isRequesting ? '処理中...' : '手配依頼をする';

  const handleClick = async () => {
    if (hasDuplications) {
      try {
        await createTripDuplicateLog({
          tripId: Number(tripId),
          displayPath: window.location.pathname,
          tripDuplications
        });
      } catch (error) {
        console.error('Failed to record trip duplicate log:', error);
      }
    }
    onArrangementRequest();
  };

  return (
    <div style={styles.container}>
      <div style={styles.messageText}>{text}</div>

      <div style={styles.summarySection}>
        <div style={styles.sectionTitle}>【選択内容】</div>
        <div style={styles.itemList}>
          {items.map(item => {
            const isHotel = item.category === SUMMARY_ITEM_CATEGORY.HOTEL;
            const checkin = formatRouteDate(item.checkinDate ?? null);
            const checkout = formatRouteDate(item.checkoutDate ?? null);
            const stayDays = item.stayDays ?? null;
            const hasHotelStayInfo = isHotel && (checkin || checkout || stayDays);
            const periodText = checkin && checkout ? `${checkin}〜${checkout}` : checkin || checkout || '';
            const nightsText = stayDays && stayDays > 0 ? `${stayDays}泊${stayDays + 1}日` : '';

            const total = includeTax ? item.priceWithTax : item.price;
            const unit = includeTax ? item.unitPriceWithTax ?? item.unitPrice : item.unitPrice;
            const showUnitBreakdown = isHotel && stayDays && stayDays > 0 && unit !== null && unit !== undefined;
            const showTaxIncluded = includeTax && item.priceWithTax !== null && item.priceWithTax !== item.price;

            return (
              <div key={item.id} style={styles.item}>
                <div style={styles.itemLabel}>{item.label}:</div>
                <div style={styles.itemContent}>
                  <div>{item.text}</div>
                  {hasHotelStayInfo && (
                    <div style={styles.hotelStayInfo}>
                      {periodText && <span>宿泊期間: {periodText}</span>}
                      {periodText && nightsText && <span> / </span>}
                      {nightsText && <span>{nightsText}</span>}
                    </div>
                  )}
                  <div style={styles.itemPrice}>
                    料金: {formatPriceWithMark(total)}
                    {showUnitBreakdown && (
                      <span>
                        （{formatPriceWithMark(unit)} × {stayDays}泊）
                      </span>
                    )}
                    {showTaxIncluded && <span>（税込 {formatPriceWithMark(item.priceWithTax)}）</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={styles.totalRow}>
        <span style={styles.totalLabel}>合計金額：</span>
        <span style={styles.totalPrice}>
          ¥{displayTotal.toLocaleString()}
          {includeTax ? '（税込）' : ''}
        </span>
      </div>

      {hasDuplications && (
        <div style={styles.duplicationBlock}>
          {tripDuplications.map(dup => (
            <div key={dup.tripId} style={styles.duplicationItem}>
              <div style={styles.duplicationTitle}>
                {dup.userName}さんの以下の旅程番号のご依頼内容と重複している可能性がございます
              </div>
              <div style={styles.duplicationDetail}>・旅程番号：{dup.tripId}</div>
              <div style={styles.duplicationDetail}>
                ・重複可能性がある旅行商品：
                {dup.elementTypes.map(type => ELEMENT_TYPE_LABELS[type] ?? type).join('、')}
              </div>
              <div style={styles.duplicationDetail}>
                申請前に申請必要性の有無をご確認いただき、申請の実行または過去の重複可能のある申請のキャンセルをお願いします。当該旅程の
                <a href={`/trips/${dup.tripId}`} target="_blank" rel="noopener noreferrer" style={styles.link}>
                  詳細はこちら
                </a>
              </div>
            </div>
          ))}
          <label style={styles.termsCheckbox}>
            <input
              type="checkbox"
              checked={duplicationAgreed}
              onChange={e => setDuplicationAgreed(e.target.checked)}
              style={styles.checkbox}
            />
            <span style={styles.termsCheckboxLabel}>重複内容を確認の上で手配を依頼する</span>
          </label>
        </div>
      )}

      <div style={styles.termsBlock}>
        <p style={styles.termsText}>
          予約（決済）完了後からご依頼いただいた各商品のキャンセル規程が適用されます。
          <br />
          ご依頼いただいた各商品について、在庫情報のタイムラグ等によりご希望に添えなかった場合は代替案をご連絡いたしますのでご了承ください。
          <br />
          <br />
          予約（決済）時に弊社の
          <a href="https://aitravel.cloud/tos/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            利用規約
          </a>
          、
          <a
            href="https://d14vxaqxdw6ji3.cloudfront.net/images/travel_terms.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            旅行業約款
          </a>
          ・
          <a
            href="https://d14vxaqxdw6ji3.cloudfront.net/images/requirements.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            手配旅行条件書
          </a>
          、
          <a
            href="https://transferdata.co.jp/privacy-policy#a"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            プライバシーポリシー
          </a>
          に同意するものとします。本依頼を行う者は、本サービスを正当に利用する権限を有することを保証します。
        </p>
        <label style={styles.termsCheckbox}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            style={styles.checkbox}
          />
          <span style={styles.termsCheckboxLabel}>上記に同意する</span>
        </label>
      </div>

      <div style={styles.buttonSection}>
        <button
          type="button"
          onClick={handleClick}
          disabled={buttonDisabled}
          style={{
            ...styles.primaryButton,
            ...(isRequested ? styles.buttonRequested : {}),
            ...(buttonDisabled ? styles.buttonDisabled : {})
          }}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

const getStyles = () => ({
  container: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '16px',
    maxWidth: '480px'
  },
  messageText: {
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.5'
  },
  summarySection: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '12px'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333'
  },
  itemList: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '12px'
  },
  item: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '4px'
  },
  itemLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#666'
  },
  itemContent: {
    fontSize: '13px',
    color: '#333',
    paddingLeft: '8px'
  },
  itemPrice: {
    fontSize: '12px',
    color: '#666',
    marginTop: '2px'
  },
  hotelStayInfo: {
    fontSize: '12px',
    color: '#666',
    marginTop: '2px'
  },
  totalRow: {
    display: 'flex' as const,
    alignItems: 'baseline' as const,
    gap: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #e0e0e0'
  },
  totalLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333'
  },
  totalPrice: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#333'
  },
  buttonSection: {
    display: 'flex' as const,
    gap: '12px',
    marginTop: '8px'
  },
  primaryButton: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: '#1976d2',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer' as const
  },
  buttonRequested: {
    backgroundColor: '#9e9e9e'
  },
  buttonDisabled: {
    cursor: 'not-allowed' as const,
    backgroundColor: '#c4c4c4',
    opacity: 1
  },
  duplicationBlock: {
    backgroundColor: '#fdecea',
    border: '1px solid #f5c6c0',
    borderRadius: '4px',
    padding: '12px',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '10px'
  },
  duplicationItem: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '4px'
  },
  duplicationTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#c62828'
  },
  duplicationDetail: {
    fontSize: '12px',
    color: '#5f2120',
    lineHeight: '1.6'
  },
  termsBlock: {
    backgroundColor: '#fafafa',
    border: '1px solid #ececec',
    borderRadius: '4px',
    padding: '12px'
  },
  termsText: {
    fontSize: '11px',
    color: '#666666',
    lineHeight: '1.7',
    margin: '0 0 10px'
  },
  termsCheckbox: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: '8px',
    cursor: 'pointer' as const,
    userSelect: 'none' as const
  },
  checkbox: {
    width: '16px',
    height: '16px',
    accentColor: '#0080cb',
    cursor: 'pointer' as const,
    margin: 0
  },
  termsCheckboxLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3a3a3a'
  },
  link: {
    color: '#0080cb',
    textDecoration: 'underline'
  }
});

export default ArrangementConfirmationMessage;
